// SPDX-License-Identifier: Apache-2.0

// SessionsLive — live Claude Code fleet, polled every 5s from
// `GET /api/sessions` (the thin claudectl --json shim added in apps/mcp).
//
// One card per running session. Click a card to expand for files-modified,
// recent errors, last activity. Status pulse:
//   green  → activity in last 30s
//   yellow → idle 30s–5min
//   gray   → stale >5min
//
// Read-only by design. No "spawn" / "kill" buttons here — those mutations
// route through the brain-agent in the side rail (the user types it).
//
// MCP unreachable case: same shape as ChatPanel's empty state — inline
// retry + the curl install hint, so a user who hasn't run /setup knows
// what to do.

import { For, Show, createMemo, createResource, createSignal, onCleanup } from "solid-js";
import { api, isMcpReachable } from "../lib/api";
import "./sessions/sessions.css";

interface SessionRow {
  pid?: number;
  project?: string;
  status?: string;
  cost_usd?: number;
  context_pct?: number;
  burn_rate_per_hr?: number;
  elapsed_secs?: number;
  tokens_in?: number;
  tokens_out?: number;
  last_error?: string | null;
  subagents?: number;
  // The full claudectl row carries more (files, recent_errors, last_activity_ts);
  // surface what we get without prescribing schema strictly.
  files_modified?: Record<string, number> | Array<{ path: string; edits: number }>;
  recent_errors?: Array<{ tool?: string; message?: string }>;
  last_activity_ts?: string | number;
  cwd?: string;
  tool_usage?: Record<string, number>;
}

interface SessionsResponse {
  sessions: SessionRow[];
  count?: number;
  warning?: string;
  unreachable?: boolean;
  installHint?: string;
  message?: string;
}

const POLL_MS = 5000;
const ENDPOINT = "/api/sessions";

async function fetchSessions(): Promise<SessionsResponse> {
  const res = await api(ENDPOINT, { headers: { accept: "application/json" } });
  // 503 means the endpoint exists but claudectl isn't installed; we still
  // want the JSON body for the install-hint surface.
  if (!res.ok && res.status !== 503) {
    throw new Error(`sessions ${res.status}`);
  }
  return (await res.json()) as SessionsResponse;
}

function lastActivityMs(row: SessionRow): number | null {
  const v = row.last_activity_ts;
  if (typeof v === "number") return v > 1e12 ? v : v * 1000;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

function pulseClass(lastMs: number | null, now: number): string {
  if (lastMs === null) return "fleet-pulse--stale";
  const age = now - lastMs;
  if (age <= 30_000) return "fleet-pulse--live";
  if (age <= 5 * 60_000) return "fleet-pulse--idle";
  return "fleet-pulse--stale";
}

function statusClass(status: string | undefined): string {
  const s = (status ?? "").toLowerCase();
  if (s.includes("process")) return "fleet-card__status--processing";
  if (s.includes("input") || s.includes("await") || s.includes("wait")) {
    return "fleet-card__status--needs-input";
  }
  return "fleet-card__status--idle";
}

function fmtElapsed(secs: number | undefined): string {
  if (typeof secs !== "number" || !Number.isFinite(secs)) return "—";
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function fmtUsd(n: number | undefined): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  if (n >= 100) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(2)}`;
}

function fmtPct(n: number | undefined): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
}

function ctxClass(pct: number | undefined): string {
  if (typeof pct !== "number") return "";
  if (pct >= 90) return "fleet-card__metric-value--danger";
  if (pct >= 70) return "fleet-card__metric-value--warn";
  return "";
}

function ctxBarClass(pct: number | undefined): string {
  if (typeof pct !== "number") return "";
  if (pct >= 90) return "fleet-card__bar-fill--danger";
  if (pct >= 70) return "fleet-card__bar-fill--warn";
  return "";
}

function topTools(usage: Record<string, number> | undefined, n = 4): Array<[string, number]> {
  if (!usage || typeof usage !== "object") return [];
  return Object.entries(usage)
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
    .slice(0, n);
}

function filesAsList(
  files: SessionRow["files_modified"]
): Array<{ path: string; edits: number }> {
  if (!files) return [];
  if (Array.isArray(files)) return files.slice(0, 10);
  return Object.entries(files)
    .map(([path, edits]) => ({ path, edits: Number(edits) || 0 }))
    .sort((a, b) => b.edits - a.edits)
    .slice(0, 10);
}

export default function SessionsLive() {
  const [data, { refetch }] = createResource(fetchSessions);
  const [expandedPid, setExpandedPid] = createSignal<number | null>(null);
  const [now, setNow] = createSignal(Date.now());

  // 5 s poll + 1 s tick for the pulse-class age gate (so green→yellow→gray
  // transitions actually happen between fetches).
  const pollInterval = setInterval(() => {
    void refetch();
  }, POLL_MS);
  const tickInterval = setInterval(() => setNow(Date.now()), 1000);
  onCleanup(() => {
    clearInterval(pollInterval);
    clearInterval(tickInterval);
  });

  const sessions = createMemo<SessionRow[]>(() => data()?.sessions ?? []);
  const count = createMemo<number>(() => sessions().length);
  // Apollo 4 — also gate on the SPA-side `isMcpReachable()` heuristic so
  // deployed Pages builds (where `/api/*` 503s synthetically) surface the
  // same explanatory banner instead of looking like a transient fetch error.
  const mcpUp = createMemo<boolean>(() => isMcpReachable());
  const unreachable = createMemo<boolean>(
    () => data()?.unreachable === true || !mcpUp(),
  );
  const warning = createMemo<string | undefined>(() => data()?.warning);
  const fetchErr = createMemo<Error | undefined>(() => data.error as Error | undefined);

  return (
    <div class="fleet">
      <div class="fleet__bar">
        <span class="fleet__count">
          {count()} {count() === 1 ? "session" : "sessions"}
        </span>
        <Show when={data.loading}>
          <span class="fleet__tick">refreshing…</span>
        </Show>
        <Show when={!data.loading}>
          <span class="fleet__tick">polling every {POLL_MS / 1000}s</span>
        </Show>
      </div>

      <Show when={fetchErr() || unreachable()}>
        <div class="fleet__error">
          <span class="fleet__error-title">
            {!mcpUp()
              ? "local backend not reachable"
              : unreachable()
                ? "claudectl not installed"
                : "mcp unreachable"}
          </span>
          <span>
            {!mcpUp()
              ? "this page is being served from a remote origin where the MCP server doesn't run. sessions are local-only — open tether at localhost (or run /setup on your machine) to see your fleet."
              : unreachable()
                ? "the brain can't see any Claude Code sessions until claudectl is on PATH. sessions are background Claude Code processes (one per project) — claudectl is the small shim that lets the brain enumerate them."
                : `couldn't reach ${ENDPOINT} — is the local MCP server running on :5417?`}
          </span>
          <pre class="fleet__error-cmd">curl tether.dev/setup | bash</pre>
          <Show when={mcpUp()}>
            <button type="button" class="fleet__retry" onClick={() => void refetch()}>
              Retry
            </button>
          </Show>
        </div>
      </Show>

      <Show when={warning() && !unreachable()}>
        <div class="fleet__error" role="alert">
          <span class="fleet__error-title">claudectl warning</span>
          <span>{warning()}</span>
        </div>
      </Show>

      <Show when={!fetchErr() && !unreachable() && count() === 0 && !data.loading}>
        <div class="fleet__empty">
          <div class="fleet__empty-eyebrow">no sessions running</div>
          <div class="fleet__empty-title">the fleet is quiet</div>
          <div class="fleet__empty-copy">
            <p>
              a <strong>session</strong> is a background Claude Code process the
              brain agent can drive through MCP — one per project, each with its
              own files-modified diary, tool-call history, and burn-rate. once
              one is running, it shows up here as a live card.
            </p>
            <p>
              <strong>three ways to start one:</strong>
            </p>
            <ul class="fleet__empty-list">
              <li>
                ask the brain at{" "}
                <a href="/chat" class="fleet__empty-link">/chat</a> — say "spawn
                a session for &lt;project&gt;" and it'll call the{" "}
                <code>spawn_session</code> MCP tool with permission.
              </li>
              <li>
                summon the temple at{" "}
                <a href="/temple" class="fleet__empty-link">/temple</a> for the
                interactive embodied surface — useful when you want to see
                tool-calls land in real time.
              </li>
              <li>
                run <code>claude</code> directly in any terminal —{" "}
                <code>claudectl</code> picks it up within a few seconds and the
                brain enumerates it through{" "}
                <code>{ENDPOINT}</code>.
              </li>
            </ul>
          </div>
        </div>
      </Show>

      <Show when={count() > 0}>
        <div class="fleet__grid">
          <For each={sessions()}>
            {(s) => {
              const isOpen = () => expandedPid() === (s.pid ?? -1);
              const lastMs = () => lastActivityMs(s);
              return (
                <button
                  type="button"
                  class="fleet-card"
                  classList={{ "fleet-card--expanded": isOpen() }}
                  onClick={() =>
                    setExpandedPid(isOpen() ? null : s.pid ?? -1)
                  }
                >
                  <div class="fleet-card__head">
                    <span
                      class={`fleet-pulse ${pulseClass(lastMs(), now())}`}
                      aria-label="activity"
                    />
                    <span class="fleet-card__project">
                      {s.project ?? "unknown project"}
                    </span>
                    <span class="fleet-card__pid">PID {s.pid ?? "?"}</span>
                  </div>

                  <div class={`fleet-card__status ${statusClass(s.status)}`}>
                    {(s.status ?? "idle").toLowerCase()}
                  </div>

                  <div class="fleet-card__metrics">
                    <div class="fleet-card__metric">
                      <span class="fleet-card__metric-label">cost</span>
                      <span class="fleet-card__metric-value">
                        {fmtUsd(s.cost_usd)}
                      </span>
                    </div>
                    <div class="fleet-card__metric">
                      <span class="fleet-card__metric-label">context</span>
                      <span class={`fleet-card__metric-value ${ctxClass(s.context_pct)}`}>
                        {fmtPct(s.context_pct)}
                      </span>
                    </div>
                    <div class="fleet-card__metric">
                      <span class="fleet-card__metric-label">elapsed</span>
                      <span class="fleet-card__metric-value">
                        {fmtElapsed(s.elapsed_secs)}
                      </span>
                    </div>
                  </div>

                  <Show when={typeof s.context_pct === "number"}>
                    <div class="fleet-card__bar" aria-label="context fill">
                      <div
                        class={`fleet-card__bar-fill ${ctxBarClass(s.context_pct)}`}
                        style={{
                          width: `${Math.max(0, Math.min(100, s.context_pct ?? 0))}%`,
                        }}
                      />
                    </div>
                  </Show>

                  <div class="fleet-card__metrics">
                    <div class="fleet-card__metric">
                      <span class="fleet-card__metric-label">burn / hr</span>
                      <span class="fleet-card__metric-value">
                        {fmtUsd(s.burn_rate_per_hr)}
                      </span>
                    </div>
                    <div class="fleet-card__metric">
                      <span class="fleet-card__metric-label">tokens in</span>
                      <span class="fleet-card__metric-value">
                        {(s.tokens_in ?? 0).toLocaleString()}
                      </span>
                    </div>
                    <div class="fleet-card__metric">
                      <span class="fleet-card__metric-label">subagents</span>
                      <span class="fleet-card__metric-value">
                        {s.subagents ?? 0}
                      </span>
                    </div>
                  </div>

                  <Show when={topTools(s.tool_usage).length > 0}>
                    <div class="fleet-card__tools">
                      <For each={topTools(s.tool_usage)}>
                        {([name, n]) => (
                          <span class="fleet-card__tool-chip">
                            <strong>{n}</strong> {name}
                          </span>
                        )}
                      </For>
                    </div>
                  </Show>

                  <Show when={isOpen()}>
                    <div class="fleet-card__expanded">
                      <Show when={s.cwd}>
                        <div class="fleet-card__exp-section">
                          <span class="fleet-card__exp-label">cwd</span>
                          <span>{s.cwd}</span>
                        </div>
                      </Show>

                      <Show when={filesAsList(s.files_modified).length > 0}>
                        <div class="fleet-card__exp-section">
                          <span class="fleet-card__exp-label">files modified</span>
                          <ul class="fleet-card__exp-list">
                            <For each={filesAsList(s.files_modified)}>
                              {(f) => (
                                <li>
                                  {f.path}{" "}
                                  <span style={{ color: "var(--text-muted)" }}>
                                    ({f.edits} edits)
                                  </span>
                                </li>
                              )}
                            </For>
                          </ul>
                        </div>
                      </Show>

                      <Show when={s.recent_errors && s.recent_errors.length > 0}>
                        <div class="fleet-card__exp-section">
                          <span class="fleet-card__exp-label">recent errors</span>
                          <ul class="fleet-card__exp-list fleet-card__exp-error">
                            <For each={(s.recent_errors ?? []).slice(0, 5)}>
                              {(e) => (
                                <li>
                                  <strong>{e.tool ?? "?"}</strong>:{" "}
                                  {(e.message ?? "").slice(0, 200)}
                                </li>
                              )}
                            </For>
                          </ul>
                        </div>
                      </Show>

                      <Show when={s.last_error}>
                        <div class="fleet-card__exp-section">
                          <span class="fleet-card__exp-label">last error</span>
                          <span class="fleet-card__exp-error">{s.last_error}</span>
                        </div>
                      </Show>

                      <Show when={lastMs() !== null}>
                        <div class="fleet-card__exp-section">
                          <span class="fleet-card__exp-label">last activity</span>
                          <span>
                            {new Date(lastMs() as number).toLocaleString()}
                          </span>
                        </div>
                      </Show>
                    </div>
                  </Show>
                </button>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}
